import type { MedaImage, ImageMetadata } from '../types/image';

const DB_NAME = 'meda-studio';
const DB_VERSION = 1;
const IMAGES_STORE = 'images';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(IMAGES_STORE)) {
        const store = db.createObjectStore(IMAGES_STORE, { keyPath: 'id' });
        store.createIndex('createdAt', 'createdAt');
        store.createIndex('source', 'source');
        store.createIndex('favorite', 'favorite');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

function tx(mode: IDBTransactionMode): Promise<IDBObjectStore> {
  return openDB().then(db => db.transaction(IMAGES_STORE, mode).objectStore(IMAGES_STORE));
}

function req<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function generateThumbnail(blob: Blob, maxSize = 200): Promise<Blob> {
  const img = await createImageBitmap(blob);
  const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = new OffscreenCanvas(w, h);
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, w, h);
  img.close();
  return canvas.convertToBlob({ type: 'image/jpeg', quality: 0.7 });
}

export async function getImageDimensions(blob: Blob): Promise<{ width: number; height: number }> {
  const img = await createImageBitmap(blob);
  const { width, height } = img;
  img.close();
  return { width, height };
}

export const imageStore = {
  async save(image: MedaImage): Promise<void> {
    const store = await tx('readwrite');
    await req(store.put(image));
  },

  async get(id: string): Promise<MedaImage | undefined> {
    const store = await tx('readonly');
    return req(store.get(id));
  },

  async delete(id: string): Promise<void> {
    const store = await tx('readwrite');
    await req(store.delete(id));
  },

  async getAll(): Promise<MedaImage[]> {
    const store = await tx('readonly');
    const index = store.index('createdAt');
    const items: MedaImage[] = await req(index.getAll());
    return items.reverse(); // newest first
  },

  async getAllMetadata(): Promise<ImageMetadata[]> {
    const images = await this.getAll();
    return images.map(({ blob: _b, thumbnail: _t, ...meta }) => meta);
  },

  async getFavorites(): Promise<MedaImage[]> {
    const all = await this.getAll();
    return all.filter(img => img.favorite);
  },

  async toggleFavorite(id: string): Promise<boolean> {
    const image = await this.get(id);
    if (!image) return false;
    image.favorite = !image.favorite;
    await this.save(image);
    return image.favorite;
  },

  async getVersionHistory(imageId: string): Promise<MedaImage[]> {
    const all = await this.getAll();
    const chain: MedaImage[] = [];
    let current = all.find(img => img.id === imageId);
    while (current) {
      chain.unshift(current);
      current = current.parentId ? all.find(img => img.id === current!.parentId) : undefined;
    }
    return chain;
  },

  async count(): Promise<number> {
    const store = await tx('readonly');
    return req(store.count());
  },
};
